import './error-template.scss';

import React, { useEffect, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import i18next from '@/i18n';
import notFoundImage from '@/media/images/not-found.jpg';
import oopsErrorImage from '@/media/images/oops-error.jpg';
import { ROUTES } from '@/shared/constants';
import { LocalizedNavLink } from '@/shared/ui';

interface IProps {
  type: ErrorType;
  redirect?: {
    text: string;
    href: string;
  } | null;
}

const ErrorTemplate: React.FC<IProps> = ({
  type,
  redirect = {
    text: i18next.t('404_redirect-text-1'),
    href: ROUTES.myOrders
  }
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { resetBoundary } = useErrorBoundary();

  const ERRORS: Record<ErrorType, ErrorsInterface> = useMemo(
    () => ({
      '404': {
        image: notFoundImage,
        title: t('404_title-1'),
        description: [t('404_description-1'), t('404_description-2')]
      },
      oops: {
        image: oopsErrorImage,
        title: t('404_title-2'),
        description: [t('404_description-3')]
      }
    }),
    [t]
  );

  useEffect(() => {
    return () => {
      resetBoundary();
    };
  }, [resetBoundary, location]);

  return (
    <div className="error-template">
      <img
        className="error-template__image"
        src={ERRORS[type].image}
        alt={`${type}-image`}
      />

      <p className="error-template__title">{ERRORS[type].title}</p>
      <p className="error-template__description">
        {ERRORS[type].description.map((item) => (
          <React.Fragment key={item}>
            {item} <br />
          </React.Fragment>
        ))}
        {redirect && (
          <LocalizedNavLink className="error-template__link" to={redirect.href}>
            {redirect.text}
          </LocalizedNavLink>
        )}
      </p>
    </div>
  );
};

export default ErrorTemplate;

type ErrorType = '404' | 'oops';

interface ErrorsInterface {
  image: string;
  title: string;
  description: string[];
}
